import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { queryClient } from "@/hooks/use-query-client";
import { ChevronRight, X } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { RootState } from "@/store/store";
import { setViewMoreDialog } from "@/store/provider-search/provider-search-slice";
import { QueryKeys } from "@/constants";
import { useFreeTextSearchViewMore } from "@/hooks/use-provider-search";
import { capitalizeInitialLetter } from "@/lib/string-util";
import type { ProviderSearchFreeTextItem } from "@/model/provider-search";

export const ListItemSkeleton = () => (
  <div className="h-12 w-full animate-pulse rounded bg-gray-200" />
);

type FreeTextViewMoreProps = {
  onSelect: (item: ProviderSearchFreeTextItem) => void;
};

const ProviderSearchFreeTextViewMore: React.FunctionComponent<
  FreeTextViewMoreProps
> = ({ onSelect }) => {
  const dispatch = useDispatch();
  const { viewMoreDialog } = useSelector(
    (state: RootState) => state.providerSearch,
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useFreeTextSearchViewMore();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries[0].isIntersecting;
        if (isVisible && hasNextPage && !isFetching && !isLoading) {
          fetchNextPage();
        }
      },
      { threshold: 1 },
    );

    const current = loadMoreRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasNextPage, fetchNextPage, isFetching, isLoading]);

  const closeViewMore = () => {
    dispatch(
      setViewMoreDialog({
        isVisible: false,
        searchBy: "",
        viewMoreSearchCriteria: "",
      }),
    );
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT],
    });
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE],
    });
  };

  return (
    <Dialog open={viewMoreDialog.isVisible} onOpenChange={closeViewMore}>
      <DialogContent
        className="px-0 pt-0 sm:max-w-[700px]"
        style={{ maxHeight: "50vh", overflow: "scroll" }}
      >
        <DialogHeader className="sticky top-0 z-10 flex h-12 flex-row items-center justify-between border-b bg-white px-4">
          <DialogTitle>{viewMoreDialog?.searchBy}</DialogTitle>
          <button
            className="text-gray-500 transition-colors hover:text-gray-700"
            onClick={closeViewMore}
          >
            <X size={20} />
          </button>
        </DialogHeader>
        <div className="space-y-2 px-2">
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <ListItemSkeleton key={i} />
              ))
            : data?.pages?.flatMap((page) =>
                page.results.data.map(
                  (item: ProviderSearchFreeTextItem, index: number) => {
                    return (
                      // 2.1.1 Keyboard — A / 4.1.2 — A: results were <div onClick>, so a
                      // keyboard user could open the "view more" list but select nothing.
                      <button
                        type="button"
                        key={index}
                        className={`w-full cursor-pointer px-4 py-2 text-left hover:bg-primary-extraLight hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${index % 2 ? "bg-white" : "bg-gray-100"}`}
                        onClick={() => onSelect(item)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-medium font-normal">
                              {capitalizeInitialLetter(item.name)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              NPI: {item.npi}
                            </div>
                          </div>
                          <div className="ml-4 flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-sm font-medium">
                                {item.distance < 1
                                  ? Math.round(item.distance * 10) / 10
                                  : Math.round(item.distance)}{" "}
                                miles
                              </div>
                              <div className="text-xs text-muted-foreground">
                                (approx.)
                              </div>
                            </div>
                            <ChevronRight className="size-4 text-muted-foreground" />
                          </div>
                        </div>
                      </button>
                    );
                  },
                ),
              )}
        </div>
        <div ref={loadMoreRef} className="h-2" />
        {isFetching && !isLoading && (
          <div className="space-y-4 px-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <ListItemSkeleton key={i} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProviderSearchFreeTextViewMore;
